/**
 * NarrationView - Script-centric dedicated view for video narration
 *
 * Design Philosophy (Industry Research - Descript, Final Cut Pro, etc.):
 * - Script as Center of Gravity - Text is the primary editing surface
 * - Split-panel layout: Scripts list (55%) + Slide panel (45%)
 * - Real slide viewer with GSAP preview and filmstrip
 * - Inline audio controls per script
 * - Typography for readability (15-16px, 1.5 line height)
 *
 * Layout:
 * - Top: Compact voice settings header
 * - Center: Split-panel with scripts list (left) and slide stage + audio controls (right)
 * - Right panel shows: SlideStage (preview + filmstrip + controls) + Audio controls
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { VoiceSettingsHeader } from './VoiceSettingsHeader';
import { ScriptsList } from './ScriptsList';
import { NarrationAudioControls, type AudioStatus as SlidePanelAudioStatus, type MasterAudioConfig } from './NarrationAudioControls';
import { NarrativeContextCard } from './NarrativeContextCard';
import { EmptySlidePanel } from './EmptySlidePanel';
import { SlideStage } from '../SlideStage';
import { useVideoWorkspaceStore, useScopedNarration, type NarrativeContext } from '@/stores/videoWorkspaceStore';
import { useNarrationJobStatus } from '@/hooks/useNarrationJobStatus';
import { useApplyJobStatus } from '@/hooks/useApplyJobStatus';
import { outlineAPI, videoManifestAPI, getApiServerUrl } from '@/services/api';
import type { NarrativeContext as NarrativeContextType } from './NarrativeContextCard';
import type { VideoManifest } from '@/types/videoManifest';
import {
  updateAudioSettings,
  generateAudio,
  getConversationAudioStatus,
  updateScript,
  applyAudioWithChoreography,
  type SlideAudioStatusSummary,
} from '@/services/audioService';
import { executeAgentJob } from '@/services/agentJobService';
import { toast } from 'sonner';
import { unifiedMessageService } from '@/services/unifiedMessageService';
import type { VideoOutline } from '@/types/outline';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface NarrationViewProps {
  conversationId: string;
  outline: VideoOutline;
  onScriptsGenerated?: () => void;
  className?: string;
}

export const NarrationView: React.FC<NarrationViewProps> = ({
  conversationId,
  outline,
  onScriptsGenerated,
  className,
}) => {
  // Connect to videoWorkspaceStore for shared slide state
  const {
    slides: storeSlides,
    currentSlideId: storeCurrentSlideId,
    setCurrentSlideId: setStoreCurrentSlideId,
    slideConversations: _slideConversations,
  } = useVideoWorkspaceStore();

  // Get cached narration data for instant loading
  const {
    cache: narrationCache,
    isLoading: cacheLoading,
    voices: cachedVoices,
    defaultVoice,
    updateScripts,
    updateAudioStatus: updateAudioStatusInCache,
    updateNarrativeContext: updateNarrativeContextInCache,
    preloadData,
  } = useScopedNarration(conversationId);

  // Derive data from cache (instant load if preloaded)
  const scripts = narrationCache?.scripts ?? [];
  const audioStatus = narrationCache?.audioStatus ?? [];
  const hasNarrativeContext = narrationCache?.hasNarrativeContext ?? false;
  const narrativeContext = narrationCache?.narrativeContext ?? null;
  const voices = cachedVoices;

  // Loading state - only show loading if no cache AND cache is loading
  const isLoading = !narrationCache && cacheLoading;

  // Audio-first mode detection
  const isAudioFirst = outline.creationMode === 'audio_first';

  // Video manifest for SlideStage
  const [videoManifest, setVideoManifest] = useState<VideoManifest | null>(null);

  // Local audio status for real-time updates (merges with cache)
  const [localAudioStatusUpdates, setLocalAudioStatusUpdates] = useState<Record<string, Partial<SlideAudioStatusSummary>>>({});

  // Merge cached audio status with local real-time updates
  const mergedAudioStatus = audioStatus.map(status => ({
    ...status,
    ...(localAudioStatusUpdates[status.slideId] || {}),
  }));

  // Voice and model settings (local state for user selections)
  const [selectedVoice, setSelectedVoice] = useState<string>(
    narrationCache?.audioSettings?.voiceId || defaultVoice || 'Kore'
  );
  const [selectedModel, setSelectedModel] = useState<'flash' | 'pro'>(
    narrationCache?.audioSettings?.ttsModel?.includes('pro') ? 'pro' : 'flash'
  );
  const [generatingSlideIds, setGeneratingSlideIds] = useState<Set<string>>(new Set());
  const [audioProgressMap, setAudioProgressMap] = useState<Record<string, number>>({});
  const audioPollingRef = useRef<NodeJS.Timeout | null>(null);

  // Audio-first: merge scriptSegment from outline into scripts so ScriptsList shows them
  const effectiveScripts = useMemo(() => {
    if (!isAudioFirst) return scripts;
    return scripts.map(script => {
      const outlineSlide = outline.slides.find(s => s.id === script.slideIdentifier);
      if (outlineSlide?.scriptSegment && !script.script?.scriptText) {
        return {
          ...script,
          script: {
            ...(script.script || { promptFlash: null, promptPro: null, preferredModel: null, audioUrl: null, audioDuration: null, updatedAt: null }),
            scriptText: outlineSlide.scriptSegment,
          },
          hasAudio: true,
        };
      }
      return script;
    });
  }, [scripts, outline.slides, isAudioFirst]);

  // Audio-first: master audio URL from video manifest
  const masterAudioUrl = videoManifest?.masterNarration?.url || null;

  // Script generation state - use hook for persistence across navigation
  const {
    isGenerating: isGeneratingScripts,
    setGenerating: setIsGeneratingScripts,
  } = useNarrationJobStatus(conversationId);
  const scriptUnsubscribeRef = useRef<(() => void) | null>(null);

  // Audio generation state
  const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);

  // Apply job status - persists across navigation and page refresh
  const {
    isApplyingAll: isApplyingAllAudio,
    applyingSlideIds,
    slideApplyProgress,
    registerJob: registerApplyJob,
    startApplyAll,
    endApplyAll,
  } = useApplyJobStatus(conversationId);


  // Script saving state
  const [isSavingScript, setIsSavingScript] = useState(false);

  // Selection and playback state - synced with store
  const [selectedSlideId, setSelectedSlideId] = useState<string | null>(null);
  const [currentlyPlayingSlideId, setCurrentlyPlayingSlideId] = useState<string | null>(null);

  // Regenerate warning dialog
  const [showRegenerateWarning, setShowRegenerateWarning] = useState(false);

  // Audio-first: compute masterAudioConfig for the selected slide
  const masterAudioConfig: MasterAudioConfig | undefined = useMemo(() => {
    if (!isAudioFirst || !masterAudioUrl || !selectedSlideId) return undefined;
    const slide = outline.slides.find(s => s.id === selectedSlideId);
    if (!slide?.audioRange) return undefined;

    const allTimings = outline.masterAudio?.wordTimings || [];
    const { startTime, endTime } = slide.audioRange;

    // Filter word timings to this slide's range and offset to 0-based
    const slideTimings = allTimings
      .filter(wt => wt.startTime >= startTime && wt.endTime <= endTime)
      .map(wt => ({
        word: wt.word,
        startTime: wt.startTime - startTime,
        endTime: wt.endTime - startTime,
        confidence: wt.confidence,
      }));

    return {
      audioUrl: masterAudioUrl,
      audioRange: slide.audioRange,
      wordTimings: slideTimings,
      scriptText: slide.scriptSegment || '',
      slideIdentifier: slide.id,
    };
  }, [isAudioFirst, masterAudioUrl, selectedSlideId, outline.slides, outline.masterAudio?.wordTimings]);

  // Prepare slides for SlideStage - map from store slides to SlideStage format
  const slidesForStage = storeSlides.map(slide => ({
    id: slide.id,
    title: slide.title,
    thumbnail: slide.thumbnail,
    duration: slide.duration,
    status: slide.status,
    childConversationId: slide.childConversationId,
  }));

  // Load video manifest for SlideStage
  useEffect(() => {
    if (!conversationId || storeSlides.length === 0) {
      setVideoManifest(null);
      return;
    }

    let cancelled = false;

    const loadManifest = async () => {
      try {
        const result = await videoManifestAPI.refreshManifest(conversationId, {
          apiBaseUrl: getApiServerUrl(),
        });
        if (!cancelled) {
          setVideoManifest(result.manifest);
        }
      } catch (error) {
        console.error('[NarrationView] Failed to load manifest:', error);
      }
    };

    loadManifest();

    return () => {
      cancelled = true;
    };
  }, [conversationId, storeSlides.length]);

  // Sync selectedSlideId with store's currentSlideId
  useEffect(() => {
    if (storeCurrentSlideId && storeCurrentSlideId !== selectedSlideId) {
      setSelectedSlideId(storeCurrentSlideId);
    }
  }, [storeCurrentSlideId]);

  // Handle slide change from SlideStage (bidirectional sync)
  const handleSlideStageChange = useCallback((slideId: string) => {
    setSelectedSlideId(slideId);
    setStoreCurrentSlideId(slideId);
  }, [setStoreCurrentSlideId]);

  // Refresh scripts from API and update cache
  const refreshScripts = useCallback(async () => {
    try {
      const response = await outlineAPI.getScripts(conversationId);
      updateScripts(conversationId, response.scripts);
    } catch (err) {
      console.error('[NarrationView] Failed to refresh scripts:', err);
    }
  }, [conversationId, updateScripts]);

  // Refresh audio status from API and update cache
  const loadAudioStatus = useCallback(async () => {
    try {
      const status = await getConversationAudioStatus(conversationId);
      updateAudioStatusInCache(conversationId, status.slides);
      // Clear local updates since we have fresh data
      setLocalAudioStatusUpdates({});

      const anyGenerating = status.slides.some(
        s => s.audioStatus === 'GENERATING' || s.audioStatus === 'ALIGNING'
      );
      if (!anyGenerating && isGeneratingAudio) {
        setIsGeneratingAudio(false);
        if (audioPollingRef.current) {
          clearInterval(audioPollingRef.current);
          audioPollingRef.current = null;
        }
        toast.success('Audio generation complete!');
      }
    } catch (err) {
      console.error('[NarrationView] Failed to load audio status:', err);
    }
  }, [conversationId, isGeneratingAudio, updateAudioStatusInCache]);

  // Trigger preload if no cache on mount (fallback for direct navigation)
  useEffect(() => {
    if (!narrationCache && conversationId) {
      console.log('[NarrationView] No cache found, triggering preload...');
      preloadData(conversationId);
    }
  }, [narrationCache, conversationId, preloadData]);

  // Initialize voice selection from cache when it becomes available
  useEffect(() => {
    if (narrationCache?.audioSettings) {
      setSelectedVoice(narrationCache.audioSettings.voiceId || defaultVoice || 'Kore');
      setSelectedModel(narrationCache.audioSettings.ttsModel?.includes('pro') ? 'pro' : 'flash');
    }
  }, [narrationCache?.audioSettings, defaultVoice]);

  // Auto-select first slide - separate from data loading
  useEffect(() => {
    if (!selectedSlideId && outline.slides.length > 0) {
      const firstActiveSlide = outline.slides.find(s => !s.isDeleted);
      if (firstActiveSlide) {
        setSelectedSlideId(firstActiveSlide.id);
      }
    }
  }, [outline.slides, selectedSlideId]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioPollingRef.current) {
        clearInterval(audioPollingRef.current);
      }
      if (scriptUnsubscribeRef.current) {
        scriptUnsubscribeRef.current();
      }
    };
  }, []);

  // Listen for scripts-batch-updated events from chat-based narration
  useEffect(() => {
    const handleScriptsBatchUpdated = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const { conversationId: eventConvId, slideIds, source } = customEvent.detail || {};
      // Only refresh if the event is for our conversation
      if (!eventConvId || eventConvId === conversationId) {
        console.log('[NarrationView] scripts-batch-updated event received, refreshing scripts', {
          slideIds,
          source,
        });
        await refreshScripts(); // Refresh and update cache

        // Show success toast if we were in generation mode
        // Note: The generating state is cleared by useNarrationJobStatus hook
        if (isGeneratingScripts) {
          toast.success('Scripts generated successfully!', { duration: 3000 });
        }

        onScriptsGenerated?.();
      }
    };

    window.addEventListener('scripts-batch-updated', handleScriptsBatchUpdated);
    return () => {
      window.removeEventListener('scripts-batch-updated', handleScriptsBatchUpdated);
    };
  }, [conversationId, refreshScripts, onScriptsGenerated, isGeneratingScripts]);

  // Listen for narrative-context-updated events from chat MessageBubble
  // This updates the TTS prompt in real-time when narrative context is generated
  useEffect(() => {
    const handleNarrativeContextUpdated = (event: Event) => {
      const customEvent = event as CustomEvent<{
        conversationId: string;
        context: NarrativeContext;
      }>;
      const { conversationId: eventConvId, context } = customEvent.detail || {};

      if (eventConvId === conversationId && context) {
        console.log('[NarrationView] narrative-context-updated event received, updating cache');
        updateNarrativeContextInCache(conversationId, context);
      }
    };

    window.addEventListener('narrative-context-updated', handleNarrativeContextUpdated);
    return () => {
      window.removeEventListener('narrative-context-updated', handleNarrativeContextUpdated);
    };
  }, [conversationId, updateNarrativeContextInCache]);

  // Listen for real-time audio events
  useEffect(() => {
    const handleAudioEvent = (event: CustomEvent) => {
      const audioEvent = event.detail?.audioEvent || {};
      // Note: AudioEvent uses 'slideIdentifier' not 'outlineSlideId'
      const { slideId, slideIdentifier, status, audioUrl, audioDuration, progress, type } = audioEvent;

      console.log('[NarrationView] Audio event received:', { type, status, progress, slideId, slideIdentifier });

      // Track progress for this slide (by both slideId and slideIdentifier for matching)
      if (progress !== undefined) {
        if (slideId) {
          setAudioProgressMap(prev => ({ ...prev, [slideId]: progress }));
        }
        if (slideIdentifier) {
          setAudioProgressMap(prev => ({ ...prev, [slideIdentifier]: progress }));
        }
      }

      // Update local audio status for real-time feedback
      if (slideId) {
        setLocalAudioStatusUpdates(prev => ({
          ...prev,
          [slideId]: {
            ...prev[slideId],
            audioStatus: status || prev[slideId]?.audioStatus,
            hasAudio: status === 'COMPLETED' || !!audioUrl || prev[slideId]?.hasAudio,
            audioUrl: audioUrl || prev[slideId]?.audioUrl,
            audioDuration: audioDuration ?? prev[slideId]?.audioDuration,
          },
        }));
      }

      // Handle completion (via status or type)
      if (status === 'COMPLETED' || status === 'FAILED' || type === 'audio_generated' || type === 'audio_error') {
        setGeneratingSlideIds(prev => {
          const next = new Set(prev);
          if (slideId) next.delete(slideId);
          return next;
        });
        // Clear progress tracking for this slide
        setAudioProgressMap(prev => {
          const newMap = { ...prev };
          if (slideId) delete newMap[slideId];
          if (slideIdentifier) delete newMap[slideIdentifier];
          return newMap;
        });
        if (status === 'COMPLETED' || type === 'audio_generated') {
          loadAudioStatus(); // Full refresh to update cache
        }
      }
    };

    window.addEventListener('audio-event', handleAudioEvent as EventListener);
    return () => window.removeEventListener('audio-event', handleAudioEvent as EventListener);
  }, [loadAudioStatus]);

  // Handle voice change
  const handleVoiceChange = async (voiceId: string) => {
    setSelectedVoice(voiceId);
    const voiceName = voices.find(v => v.id === voiceId)?.name || voiceId;
    try {
      await updateAudioSettings(conversationId, { voiceId, voiceName });
    } catch (err) {
      console.error('[NarrationView] Failed to update voice:', err);
    }
  };

  // Handle model change
  const handleModelChange = async (model: 'flash' | 'pro') => {
    setSelectedModel(model);
    try {
      const ttsModel = model === 'pro' ? 'gemini-2.5-pro-tts' : 'gemini-2.5-flash-tts';
      await updateAudioSettings(conversationId, { ttsModel });
    } catch (err) {
      console.error('[NarrationView] Failed to update model:', err);
    }
  };

  // Handle narrative context prompt save - now using single audioPrompt
  const handleSaveNarrativePrompt = useCallback(async (prompt: string) => {
    try {
      // Use single audioPrompt field (new simplified approach)
      const updates = { audioPrompt: prompt };

      const result = await outlineAPI.updateNarrativeContext(conversationId, updates);

      // Update the cache with the new narrative context
      if (result.narrativeContext) {
        updateNarrativeContextInCache(conversationId, result.narrativeContext as NarrativeContext);
      }
    } catch (err) {
      console.error('[NarrationView] Failed to save narrative prompt:', err);
      throw err; // Re-throw so the card can show error state
    }
  }, [conversationId, updateNarrativeContextInCache]);

  // Handle generate scripts click
  const handleGenerateScriptsClick = () => {
    const hasExistingScripts = scripts.some(s => s.script?.scriptText);
    if (hasExistingScripts) {
      setShowRegenerateWarning(true);
    } else {
      startScriptGeneration();
    }
  };

  // Start script generation via chat message
  const startScriptGeneration = async (regenerate = false) => {
    setShowRegenerateWarning(false);
    setIsGeneratingScripts(true); // Mark as generating (persists across navigation)

    try {
      // Build the prompt based on whether this is regeneration or initial generation
      const prompt = regenerate
        ? 'Regenerate all narration scripts for this video. Use a professional and engaging tone. Improve upon the existing scripts.'
        : 'Generate narration scripts for all slides in this video. Use a professional and engaging tone.';

      console.log('[NarrationView] Starting script generation via chat message');

      // Send chat message with narration context
      // The unifiedMessageService handles SSE connection internally
      // The useNarrationJobStatus hook will detect the active job via polling
      await unifiedMessageService.sendMessage({
        conversationId,
        content: prompt,
        context: {
          source: 'narration',
          narrationAction: 'generate',
        },
      });

      // UI will update via Redis pub/sub events (scripts-batch-updated)
      // The isGeneratingScripts state will be cleared by the event handler (via useNarrationJobStatus)
      console.log('[NarrationView] Script generation message sent, waiting for SSE events');
    } catch (err) {
      console.error('[NarrationView] Failed to start script generation:', err);
      setIsGeneratingScripts(false);
      toast.error('Failed to start script generation. Please try again.', {
        duration: 5000,
      });
    }
  };

  // Handle generate all audio
  const handleGenerateAllAudio = async () => {
    setIsGeneratingAudio(true);

    const slidesWithScripts = scripts.filter(s => s.script?.scriptText && s.slideId);
    if (slidesWithScripts.length === 0) {
      toast.error('No slides with scripts to generate audio for');
      setIsGeneratingAudio(false);
      return;
    }

    setGeneratingSlideIds(new Set(slidesWithScripts.map(s => s.slideId)));

    try {
      const voiceName = voices.find(v => v.id === selectedVoice)?.name || selectedVoice;
      const ttsModel = selectedModel === 'pro' ? 'pro' : 'flash';

      let queued = 0;
      for (const slideScript of slidesWithScripts) {
        try {
          await generateAudio(slideScript.slideId, { voiceId: selectedVoice, voiceName, ttsModel });
          queued++;
        } catch (err) {
          console.error(`[NarrationView] Failed to queue audio for ${slideScript.slideIdentifier}:`, err);
        }
      }

      if (queued > 0) {
        toast.info(`Queued audio generation for ${queued} slides...`);
        audioPollingRef.current = setInterval(loadAudioStatus, 2000);
      } else {
        toast.error('Failed to queue any audio generation');
        setIsGeneratingAudio(false);
      }
    } catch (err) {
      console.error('[NarrationView] Failed to generate all audio:', err);
      setIsGeneratingAudio(false);
    }
  };

  // Handle single slide audio generation
  const handleGenerateSlideAudio = async (slideId: string) => {
    const script = scripts.find(s => s.slideId === slideId);
    if (!script?.script?.scriptText) return;

    setGeneratingSlideIds(prev => new Set(prev).add(slideId));

    try {
      const voiceName = voices.find(v => v.id === selectedVoice)?.name || selectedVoice;
      const ttsModel = selectedModel === 'pro' ? 'pro' : 'flash';
      await generateAudio(slideId, { voiceId: selectedVoice, voiceName, ttsModel });
      toast.info('Audio generation started...');
    } catch (err) {
      console.error('[NarrationView] Failed to generate audio:', err);
      toast.error('Failed to generate audio');
      setGeneratingSlideIds(prev => {
        const next = new Set(prev);
        next.delete(slideId);
        return next;
      });
    }
  };

  // Handle script save (now accepts slideId)
  const handleScriptSave = async (slideId: string, text: string) => {
    setIsSavingScript(true);
    try {
      await updateScript(slideId, text);

      // Update the cache with the new script text
      const updatedScripts = scripts.map(s =>
        s.slideId === slideId
          ? { ...s, script: { ...s.script!, scriptText: text, updatedAt: new Date().toISOString() } }
          : s
      );
      updateScripts(conversationId, updatedScripts);

      window.dispatchEvent(new CustomEvent('script-updated', {
        detail: { conversationId, slideId, scriptText: text },
      }));

      toast.success('Script saved');
    } catch (err) {
      console.error('[NarrationView] Failed to save script:', err);
      toast.error('Failed to save script');
    } finally {
      setIsSavingScript(false);
    }
  };


  // Playback handler
  const handlePlayChange = (slideId: string, playing: boolean) => {
    if (playing) {
      // Stop any other playing audio first
      if (currentlyPlayingSlideId && currentlyPlayingSlideId !== slideId) {
        setCurrentlyPlayingSlideId(null);
      }
      setCurrentlyPlayingSlideId(slideId);
    } else {
      setCurrentlyPlayingSlideId(prev => prev === slideId ? null : prev);
    }
  };

  // Listen for Apply All completion event from the hook
  // The hook auto-ends Apply All when the last job completes
  useEffect(() => {
    const handleApplyAllCompleted = (event: Event) => {
      const customEvent = event as CustomEvent<{ conversationId: string }>;
      if (customEvent.detail?.conversationId === conversationId) {
        console.log('[NarrationView] Apply All completed via hook event');
        toast.success('Audio applied to all slides!');
        loadAudioStatus();
      }
    };

    window.addEventListener('apply-all-completed', handleApplyAllCompleted);
    return () => window.removeEventListener('apply-all-completed', handleApplyAllCompleted);
  }, [conversationId, loadAudioStatus]);

  // Calculate stats
  const activeSlides = outline.slides.filter(s => !s.isDeleted);
  const totalScripts = effectiveScripts.filter(s => s.script?.scriptText).length;
  const scriptsWithAudio = isAudioFirst ? totalScripts : mergedAudioStatus.filter(s => s.audioStatus === 'COMPLETED').length;
  const audioGeneratingCount = mergedAudioStatus.filter(s => s.audioStatus === 'GENERATING' || s.audioStatus === 'ALIGNING').length;

  // Calculate slides needing audio applied (completed audio but not yet applied)
  // Audio-first: all slides with audioRange need sync
  const slidesNeedingApply = isAudioFirst
    ? outline.slides.filter(s => !s.isDeleted && s.audioRange).length
    : mergedAudioStatus.filter(s => s.audioStatus === 'COMPLETED').length;

  // Handle apply all audio - runs apply logic in PARALLEL for all slides
  // Uses shared applyAudioWithChoreography function + useApplyJobStatus hook
  // This ensures consistent behavior between individual Apply and Apply All
  const handleApplyAllAudio = useCallback(async () => {
    console.log('[NarrationView] handleApplyAllAudio called');

    // Match slides using both slideId and slideIdentifier
    // IMPORTANT: Filter out slides without valid slideId (empty string means slide doesn't exist yet)
    const slidesToApply = effectiveScripts.filter(s => {
      // Must have a valid slideId (not empty string) for choreography job to work
      if (!s.slideId || s.slideId.length === 0) {
        console.warn(`[NarrationView] Skipping slide ${s.slideIdentifier} - no slideId (slide not generated yet)`);
        return false;
      }

      const status = mergedAudioStatus.find(a =>
        a.slideId === s.slideId ||
        a.slideIdentifier === s.slideIdentifier
      );
      return status?.audioStatus === 'COMPLETED';
    });

    if (slidesToApply.length === 0) {
      toast.error('No slides with completed audio to apply');
      return;
    }

    toast.info(`Applying audio to ${slidesToApply.length} slides...`, { duration: 2000 });

    // Mark Apply All as active (persisted in store)
    startApplyAll();

    // Use shared function for each slide (same logic as individual Apply)
    const results = await Promise.all(
      slidesToApply.map(slide =>
        applyAudioWithChoreography({
          slideId: slide.slideId,
          slideIdentifier: slide.slideIdentifier,
          conversationId,
          scriptText: slide.script?.scriptText || '',
          registerJob: registerApplyJob,
        })
      )
    );

    const choreographyJobsCreated = results.filter(r => r.choreographyJobCreated).length;
    console.log(`[NarrationView] Choreography jobs created: ${choreographyJobsCreated}`);

    // If no choreography jobs were needed, end Apply All immediately
    if (choreographyJobsCreated === 0) {
      console.log('[NarrationView] No choreography jobs needed, completing Apply All');
      endApplyAll();
      toast.success('Audio applied to all slides!');
    }
    // Otherwise, the hook will auto-end Apply All when the last job completes

    loadAudioStatus();
  }, [effectiveScripts, mergedAudioStatus, conversationId, loadAudioStatus, startApplyAll, endApplyAll, registerApplyJob]);

  // Handle sync all animations (audio-first mode)
  // Triggers choreography directly for each slide with audioRange
  const handleSyncAllAnimations = useCallback(async () => {
    console.log('[NarrationView] handleSyncAllAnimations called');

    const slidesWithAudioRange = outline.slides.filter(s =>
      !s.isDeleted && s.audioRange && outline.masterAudio?.wordTimings
    );

    if (slidesWithAudioRange.length === 0) {
      toast.error('No slides with audio ranges to sync');
      return;
    }

    // Check that all slides have DB records (slideId) for choreography
    const slidesToSync = slidesWithAudioRange.filter(s => {
      const script = effectiveScripts.find(sc => sc.slideIdentifier === s.id);
      if (!script?.slideId || script.slideId.length === 0) {
        console.warn(`[NarrationView] Skipping slide ${s.id} - no slideId`);
        return false;
      }
      return true;
    });

    if (slidesToSync.length === 0) {
      toast.error('No generated slides available to sync');
      return;
    }

    toast.info(`Syncing animations for ${slidesToSync.length} slides...`, { duration: 2000 });
    startApplyAll();

    const allTimings = outline.masterAudio?.wordTimings || [];

    for (const slide of slidesToSync) {
      const { startTime, endTime } = slide.audioRange!;
      const slideTimings = allTimings
        .filter(wt => wt.startTime >= startTime && wt.endTime <= endTime)
        .map(wt => ({
          word: wt.word,
          startTime: wt.startTime - startTime,
          endTime: wt.endTime - startTime,
          confidence: wt.confidence,
        }));

      if (slideTimings.length === 0) continue;

      const script = effectiveScripts.find(sc => sc.slideIdentifier === slide.id);
      const dbSlideId = script?.slideId || '';

      try {
        const response = await executeAgentJob({
          conversationId,
          agentName: 'animation_choreographer',
          inputs: {
            slideDirectory: `slides/${slide.id}`,
            wordTimings: JSON.stringify(slideTimings),
            audioDuration: endTime - startTime,
            scriptText: slide.scriptSegment || '',
          },
          metadata: {
            slideId: dbSlideId,
            slideIdentifier: slide.id,
          },
        });

        registerApplyJob(dbSlideId, response.job.id);
      } catch (err) {
        console.error(`[NarrationView] Failed to trigger choreography for ${slide.id}:`, err);
      }
    }
  }, [outline, effectiveScripts, conversationId, startApplyAll, registerApplyJob]);

  // Helper functions for NarrationAudioControls
  const getSlideIdForOutlineSlide = useCallback((outlineSlideId: string): string => {
    const script = effectiveScripts.find(s => s.slideIdentifier === outlineSlideId);
    return script?.slideId || '';
  }, [effectiveScripts]);

  const getScriptText = useCallback((outlineSlideId: string): string | null => {
    const script = effectiveScripts.find(s => s.slideIdentifier === outlineSlideId);
    return script?.script?.scriptText || null;
  }, [effectiveScripts]);

  const getAudioStatusForPanel = useCallback((outlineSlideId: string): SlidePanelAudioStatus => {
    if (isAudioFirst) return 'completed'; // Master audio always available

    const script = effectiveScripts.find(s => s.slideIdentifier === outlineSlideId);
    const status = mergedAudioStatus.find(s =>
      s.slideIdentifier === outlineSlideId ||
      (script?.slideId && s.slideId === script.slideId)
    );

    // Check if force generating
    if (script?.slideId && generatingSlideIds.has(script.slideId)) {
      return 'generating';
    }

    if (!status?.audioStatus) return 'none';
    switch (status.audioStatus) {
      case 'COMPLETED': return 'completed';
      case 'GENERATING': return 'generating';
      case 'ALIGNING': return 'aligning';
      case 'FAILED': return 'failed';
      case 'PENDING': return 'pending';
      default: return 'none';
    }
  }, [effectiveScripts, mergedAudioStatus, generatingSlideIds, isAudioFirst]);


  // Handle play change from slide panel
  const handleSlidePanelPlayChange = useCallback((playing: boolean) => {
    if (!selectedSlideId) return;
    const slideId = getSlideIdForOutlineSlide(selectedSlideId);
    if (slideId) {
      handlePlayChange(slideId, playing);
    }
  }, [selectedSlideId, getSlideIdForOutlineSlide, handlePlayChange]);

  // Handle generate audio from slide panel
  const handleSlidePanelGenerateAudio = useCallback(() => {
    if (!selectedSlideId) return;
    const slideId = getSlideIdForOutlineSlide(selectedSlideId);
    if (slideId) {
      handleGenerateSlideAudio(slideId);
    }
  }, [selectedSlideId, getSlideIdForOutlineSlide, handleGenerateSlideAudio]);

  // Get currently playing state for the selected slide
  const isSelectedSlidePlaying = selectedSlideId
    ? currentlyPlayingSlideId === getSlideIdForOutlineSlide(selectedSlideId)
    : false;

  // Get current slide info for header display
  const currentSlideInfo = selectedSlideId
    ? outline.slides.find(s => s.id === selectedSlideId)
    : null;
  const currentSlideIndex = selectedSlideId
    ? outline.slides.filter(s => !s.isDeleted).findIndex(s => s.id === selectedSlideId) + 1
    : 0;

  return (
    <div className={cn("flex flex-col h-full studio-canvas", className)}>
      {/* Voice Settings Header (compact) */}
      <VoiceSettingsHeader
        voices={voices}
        selectedVoice={selectedVoice}
        onVoiceChange={handleVoiceChange}
        selectedModel={selectedModel}
        onModelChange={handleModelChange}
        totalScripts={totalScripts}
        scriptsWithAudio={scriptsWithAudio}
        isGeneratingScripts={isGeneratingScripts}
        isGeneratingAudio={isGeneratingAudio}
        audioGeneratingCount={audioGeneratingCount}
        hasNarrativeContext={hasNarrativeContext}
        onGenerateScripts={handleGenerateScriptsClick}
        onGenerateAudio={handleGenerateAllAudio}
        onApplyAllAudio={isAudioFirst ? handleSyncAllAnimations : handleApplyAllAudio}
        isApplyingAllAudio={isApplyingAllAudio}
        slidesNeedingApply={slidesNeedingApply}
        isAudioFirst={isAudioFirst}
        masterAudioDuration={outline.masterAudio?.duration}
      />

      {/* Main Content: Split-panel layout with floating glass panels */}
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col md:flex-row gap-2 md:gap-3 p-2 md:p-3">
        {/* Left Panel: Scripts List (40% on desktop, full width on mobile) - floating glass panel */}
        <motion.div
          className="w-full md:w-[40%] min-h-0 overflow-hidden flex flex-col studio-panel-elevated rounded-xl max-h-[45%] md:max-h-none"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -100, opacity: 0 }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30,
            delay: 0.1,
          }}
        >
          {/* Narrative Context Card - Collapsible header showing voice style */}
          {hasNarrativeContext && narrativeContext && (
            <div className="flex-shrink-0 px-3 pt-3">
              <NarrativeContextCard
                context={narrativeContext as NarrativeContextType}
                selectedModel={selectedModel}
                onSavePrompt={handleSaveNarrativePrompt}
                isLoading={isLoading}
              />
            </div>
          )}
          <div className="flex-1 min-h-0 overflow-hidden">
            <ScriptsList
              outlineSlides={activeSlides}
              scripts={effectiveScripts}
              audioStatus={mergedAudioStatus}
              generatingSlideIds={generatingSlideIds}
              audioProgressMap={audioProgressMap}
              selectedSlideId={selectedSlideId}
              currentlyPlayingSlideId={currentlyPlayingSlideId}
              isLoading={isLoading}
              applyingSlideIds={applyingSlideIds}
              slideApplyProgress={slideApplyProgress}
              onSelectSlide={(slideId) => {
                setSelectedSlideId(slideId);
                setStoreCurrentSlideId(slideId);
              }}
              onGenerateAudio={handleGenerateSlideAudio}
              onPlayChange={handlePlayChange}
              onScriptSave={isAudioFirst ? undefined : handleScriptSave}
              isSavingScript={isSavingScript}
            />
          </div>
        </motion.div>

        {/* Right Panel: Slide Preview (60% on desktop, full width on mobile) - floating glass panel */}
        <motion.div
          className="w-full md:w-[60%] min-h-0 overflow-hidden flex flex-col studio-panel-elevated rounded-xl flex-1"
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 30,
            delay: 0.15,
          }}
        >
          {slidesForStage.length > 0 ? (
            <>
              {/* Slide Header: Number + Title */}
              <motion.div
                className="flex-shrink-0 px-3 md:px-5 pt-3 md:pt-4 pb-2 md:pb-3"
                initial={{ y: -10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 300,
                  damping: 30,
                  delay: 0.12,
                }}
              >
                <div className="flex items-center gap-3">
                  {currentSlideIndex > 0 && (
                    <span className="flex items-center justify-center w-7 h-7 rounded-lg text-primary-foreground text-xs font-semibold shadow-sm bg-primary">
                      {currentSlideIndex}
                    </span>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-zinc-100 truncate">
                      {currentSlideInfo?.title || `Slide ${currentSlideIndex}`}
                    </h3>
                    {currentSlideInfo?.layoutId && (
                      <p className="text-[11px] text-gray-500 dark:text-zinc-500 truncate mt-0.5">
                        {currentSlideInfo.layoutId.replace(/_/g, ' ').replace(/layout\s*/i, '')}
                      </p>
                    )}
                  </div>
                </div>
              </motion.div>

              {/* SlideStage: Real slide viewer with filmstrip and player controls */}
              <div className="flex-1 min-h-0 px-3 md:px-5 pb-3 md:pb-4">
                <div className="h-full rounded-xl overflow-hidden shadow-sm ring-1 ring-black/5 dark:ring-white/5 bg-white dark:bg-zinc-900">
                  <SlideStage
                    conversationId={conversationId}
                    currentSlideId={selectedSlideId}
                    slides={slidesForStage}
                    videoManifest={videoManifest}
                    onSlideChange={handleSlideStageChange}
                    showFilmstrip={true}
                    showPlayerControls={true}
                    className="h-full"
                  />
                </div>
              </div>

              {/* Audio Controls: Generate, play, apply audio - slides up from bottom */}
              {(() => {
                const dbSlideId = selectedSlideId ? getSlideIdForOutlineSlide(selectedSlideId) : '';
                // In audio-first mode, show controls even without dbSlideId if masterAudioConfig is available
                const showControls = selectedSlideId && (dbSlideId || masterAudioConfig);
                if (showControls) {
                  return (
                    <motion.div
                      className="flex-shrink-0 px-3 md:px-5 pb-3 md:pb-4"
                      initial={{ y: 20, opacity: 0 }}
                      animate={{ y: 0, opacity: 1 }}
                      transition={{
                        type: "spring",
                        stiffness: 300,
                        damping: 30,
                        delay: 0.15,
                      }}
                    >
                      <div className="rounded-xl overflow-hidden bg-white dark:bg-zinc-900 shadow-sm ring-1 ring-black/5 dark:ring-white/5">
                        <NarrationAudioControls
                          slideId={dbSlideId}
                          slideIdentifier={selectedSlideId!}
                          conversationId={conversationId}
                          scriptText={getScriptText(selectedSlideId!)}
                          audioStatus={isAudioFirst ? 'completed' : getAudioStatusForPanel(selectedSlideId!)}
                          isPlaying={isSelectedSlidePlaying}
                          onPlayChange={handleSlidePanelPlayChange}
                          onGenerateAudio={handleSlidePanelGenerateAudio}
                          onAudioApplied={loadAudioStatus}
                          masterAudioConfig={masterAudioConfig}
                        />
                      </div>
                    </motion.div>
                  );
                }
                return null;
              })()}
            </>
          ) : (
            <EmptySlidePanel />
          )}
        </motion.div>
      </div>

      {/* Regenerate Warning Dialog */}
      <Dialog open={showRegenerateWarning} onOpenChange={setShowRegenerateWarning}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Regenerate All Scripts?</DialogTitle>
            <DialogDescription>
              This will overwrite all existing scripts. Your manual edits will be lost.
              Audio files will remain until you regenerate them individually.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRegenerateWarning(false)}>
              Cancel
            </Button>
            <Button onClick={() => startScriptGeneration(true)}>
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NarrationView;
